// Pure decision helpers for the Log Food unsaved-cart ("basket") navigation guard.
//
// The cart state lives in LogFood.jsx. These helpers keep the leave logic testable and free of
// React / router side effects so we can assert the exact behaviour required:
//  - only prompt when the cart has unlogged foods AND the user is actually leaving the flow;
//  - "Log meal" reuses the SAME commit functions the final Log button uses (no duplicate logging);
//  - "Discard and leave" clears the cart and continues to the originally requested destination.

export function shouldGuardLeave(basketCount) {
  return (basketCount || 0) > 0;
}

// A destination is "leaving the flow" when it routes to a different base path than the Log Food
// flow. Internal view switches (hub <-> confirm <-> restaurant menu) never change the route, so
// they never reach here.
export function isLeavingFlow(destination, flowBasePath) {
  if (!destination) return true;
  return String(destination).split('?')[0] !== flowBasePath;
}

// Decide whether a leave attempt should block (show the dialog) or proceed immediately.
export function planLeave(basketCount, destination) {
  return { block: shouldGuardLeave(basketCount), destination: destination };
}

// Orchestrate the dialog's "Log meal" action. Reuses the caller-supplied commit functions:
//  - a single-item cart must be reviewed (serving confirmation) -> route to review, defer the nav;
//  - a 2+ item cart logs atomically via commitGroup, which navigates to `destination` on success
//    and (on failure) leaves the cart intact and keeps the user in the flow.
export async function runLeaveLogMeal(opts) {
  var basket = (opts && opts.basket) || [];
  if (basket.length === 0) return { action: 'none' };
  if (basket.length === 1) {
    opts.commitSingle(basket[0], opts.destination);
    return { action: 'review' };
  }
  await opts.commitGroup(basket, opts.destination);
  return { action: 'logged' };
}

// "Discard and leave": clear the unlogged cart, then continue to the destination.
export function runDiscardAndLeave(opts) {
  opts.clearBasket();
  opts.navigate(opts.destination);
}

// Wrap an async action so repeated taps can never trigger a second in-flight run (no duplicate logs).
export function createSingleFlight(fn) {
  var inFlight = false;
  return async function () {
    if (inFlight) return { skipped: true };
    inFlight = true;
    try {
      return await fn.apply(null, arguments);
    } finally {
      inFlight = false;
    }
  };
}

// --- History back-guard lifecycle (pure decisions used by LogFood's popstate handling) ---

// Push a marked history entry only when the cart just became non-empty AND no guard entry exists
// yet. The presence of the marker on history.state is the source of truth, so add→clear→add never
// accumulates more than one guard entry.
export function historyGuardShouldPush(basketNonEmpty, guardAlreadyPresent) {
  return !!basketNonEmpty && !guardAlreadyPresent;
}

// On a Back press: block + prompt while the cart holds unlogged food; otherwise allow the Back.
export function backPressDecision(basketCount) {
  return (basketCount || 0) > 0 ? 'block-and-prompt' : 'allow';
}

// Resolve the destination after a successful single-food log: the leave-guard's pending destination
// when present, else the flow's returnPath.
export function resolvePostLogDestination(postLogDestination, returnPath) {
  return postLogDestination || returnPath;
}

// Push EXACTLY ONE marked history entry for the active flow. The marker on history.state is the
// source of truth, so calling this repeatedly (add → clear → add) never accumulates entries.
export function ensureBackGuard(win) {
  if (!win || !win.history) return;
  var st = win.history.state;
  if (!(st && st.__fitxessLogFoodGuard)) {
    win.history.pushState(
      Object.assign({}, st || {}, { __fitxessLogFoodGuard: true }), '', win.location.href);
  }
}

// Build the popstate handler: while the cart still holds unlogged food, re-assert the guard entry
// (the browser already moved back one entry) and prompt; once the cart is empty the Back proceeds.
export function makeBackPopHandler(win, getBasketCount, onPrompt) {
  return function () {
    if ((getBasketCount() || 0) > 0) {
      ensureBackGuard(win);   // re-assert the entry the browser just popped
      onPrompt();
    }
    // cart empty -> allow the Back (guard is inert; no re-push, no prompt)
  };
}
